import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@shared-kernel/primary/shared/shadcn/ui/dialog";
import { Button } from "@shared-kernel/primary/shared/shadcn/ui/button";
import { formatToCurrency } from "@utils/formatting";
import { UnpaidLessonsCardList } from "./unpaid-lessons-card-list";
import { PayoutRequestDialogContainer } from "./payout-request-dialog.container";
import { Link } from "react-router-dom";
import { ROLE_BASED_URLS } from "src/routes";
import { ROLES } from "@user-management-context/shared/domain/types/enums/roles";
import { isLoadingListState } from "@utils/utils";
import { ActivityCardSkeleton } from "@shared-kernel/primary/shared/card-list/activity-card-skeleton.content";
import { EmptyState } from "@shared-kernel/primary/shared/layout/state-components/empty-state";
import type { UnpaidLessonVM } from "@shared-kernel/application/ports/teacher/teacher-repository";
import type { RequestState } from "@redux/state/shared-types";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  unpaidLessons: UnpaidLessonVM[] | null;
  fetching: RequestState;
}

export const UnpaidLessonsDialog = ({ open, onOpenChange, unpaidLessons, fetching }: Props) => {
  const [isPayoutRequestOpen, setIsPayoutRequestOpen] = useState(false);

  const totalHours = unpaidLessons?.reduce((sum, lesson) => sum + lesson.hours, 0) || 0;
  const totalAmount = unpaidLessons?.reduce((sum, lesson) => sum + lesson.amount, 0) || 0;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl gap-0">
          <DialogHeader>
            <DialogTitle>Cours non réglés</DialogTitle>
            <DialogDescription>
              Total : {totalHours} heures • {formatToCurrency(totalAmount)}
            </DialogDescription>
            <div className="grid w-full grid-cols-5 items-center justify-between space-x-3">
              <div className="col-span-4 text-sm font-bold">
                Pour supprimer un cours,
                <Link
                  to={ROLE_BASED_URLS[ROLES.TEACHER]["lesson"].list}
                  className="mx-1 text-blue-600 underline hover:text-blue-800"
                  onClick={() => onOpenChange(false)}
                >
                  <span>cliquez ici</span>
                </Link>{" "}
              </div>
              <div>
                {unpaidLessons && unpaidLessons.length > 0 && (
                  <Button onClick={() => setIsPayoutRequestOpen(true)}>Demander le règlement</Button>
                )}
              </div>
            </div>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto">
            {isLoadingListState(fetching) ? (
              <div className="space-y-2">
                <ActivityCardSkeleton />
                <ActivityCardSkeleton />
                <ActivityCardSkeleton />
              </div>
            ) : unpaidLessons && unpaidLessons.length > 0 ? (
              <UnpaidLessonsCardList lessons={unpaidLessons} />
            ) : (
              <EmptyState title="Aucun cours non réglé" description="Vous n'avez pas de cours en attente de paiement" />
            )}
          </div>
        </DialogContent>
      </Dialog>
      <PayoutRequestDialogContainer
        open={isPayoutRequestOpen}
        onOpenChange={setIsPayoutRequestOpen}
        parentDialogOnOpenChange={onOpenChange}
        hours={totalHours}
        amount={totalAmount}
      />
    </>
  );
};
